import React, { useContext } from 'react';
import { Button } from '../ui/button';
import { FormsApi } from 'api-client/apis';
import { getApiConfiguration } from '../../api/utils';
import { useTeams } from '../../contexts/TeamsContext';
import { AuthContext } from '../../contexts/AuthContext';
import { toast } from 'sonner';

interface FeaturePreviewProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  featureName: string;
  actionButton?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

export const FeaturePreview: React.FC<FeaturePreviewProps> = ({
  title,
  description,
  icon,
  featureName,
  actionButton,
  children,
  className = '',
}) => {
  const { currentTeam } = useTeams();
  const { token } = useContext(AuthContext);
  const [isLoading, setIsLoading] = React.useState(false);
  const [requestSent, setRequestSent] = React.useState(false);

  const handleUpgradeClick = async () => {
    if (!currentTeam?.slug || !token) {
      toast.error("Unable to send upgrade request. Please try again later.");
      return;
    }

    setIsLoading(true);
    
    try {
      // Use the FormsApi to submit the upgrade request
      const api = new FormsApi(getApiConfiguration(token));
      
      // Create the form submission with the upgrade request details
      // The API expects a FormSubmission object with specific fields
      // The 'id', 'created_at', and 'updated_at' fields are handled by the backend
      await api.formsSubmitCreate({
        teamSlug: currentTeam.slug,
        formSubmission: {
          title: `Feature Upgrade Request: ${title}`,
          sourceIp: window.location.hostname || '127.0.0.1',
          content: {
            featureName: featureName,
            featureTitle: title,
            teamName: currentTeam.name,
            teamSlug: currentTeam.slug,
            requestType: 'upgrade_request',
            timestamp: new Date().toISOString(),
            requestUrl: window.location.href,
            userAgent: navigator.userAgent
          }
        } as any // Type assertion to bypass TypeScript error
      });
      
      setRequestSent(true);
      toast.success("Your upgrade request has been sent to the team.");
      
    } catch (error) {
      console.error('Error sending upgrade request:', error);
      toast.error("Unable to send upgrade request. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`flex flex-col items-center justify-center min-h-[70vh] px-4 ${className}`}>
      <div className="bg-gray-800 p-8 rounded-lg max-w-3xl w-full text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-blue-100 mb-6">
          {icon}
        </div>
        <h2 className="text-2xl font-bold text-white mb-3">{title}</h2>
        <p className="text-gray-400 mb-8 max-w-3xl mx-auto">{description}</p>
        
        {children}
        
        {actionButton || (
          <Button 
            variant="default"
            onClick={handleUpgradeClick}
            className="px-6 py-2 mt-4 h-12"
            disabled={isLoading || requestSent}
          >
            {isLoading ? "Sending Request..." : requestSent ? "Request Sent" : "Upgrade to Access"}
          </Button>
        )}
      </div>
    </div>
  );
};
